const WIDGET_VERSION = '2.0.0';

/**
 * Keeps the Payflex calculator in sync with the selected variant.
 *
 * The first render comes from `snippets/payflex-widget.liquid`. On a variant change Horizon
 * morphs the new variant id onto the hidden `input[ref="variantId"]` but leaves this element's
 * attributes untouched, so that input is the signal: watch it, look the new price up in the
 * Liquid-rendered price map, and re-inject the vendor script.
 *
 * The vendor script only builds its widget when no `#payflexCalculatorWidget` is on the page,
 * and appends a fresh `#payflex-modal` to the body each time, so both are cleared before a
 * re-render or the second one silently no-ops behind a stale modal.
 */
class PayflexWidget extends HTMLElement {
  static observedAttributes = ['data-merchant', 'data-amount', 'data-min', 'data-max'];

  #rendered = null;
  #variantId = null;

  connectedCallback() {
    if (this.#rendered !== null) return;

    // The inline script already covers the current price — take it as the baseline.
    this.#rendered = this.#key();
    this.#watchVariant();
  }

  attributeChangedCallback() {
    // Upgrade replays every observed attribute before connectedCallback runs. Those calls
    // arrive with no baseline set, and acting on them injects a second vendor script that
    // races the inline one into rendering a duplicate widget.
    if (this.#rendered === null || !this.isConnected) return;
    if (this.#key() === this.#rendered) return;

    this.#rendered = this.#key();
    this.#render();
  }

  #watchVariant() {
    const form = this.closest('product-form-component');
    const prices = this.#prices();
    if (!form || !prices) return;

    const currentId = () => form.querySelector('input[ref="variantId"]')?.value ?? null;
    this.#variantId = currentId();

    // subtree, so a morph that swaps the input for a new node is still picked up.
    new MutationObserver(() => {
      const id = currentId();
      if (!id || id === this.#variantId) return;

      this.#variantId = id;
      const amount = prices[id];
      // Setting the attribute re-enters attributeChangedCallback, which does the render.
      if (amount) this.dataset.amount = amount;
    }).observe(form, { attributes: true, attributeFilter: ['value'], subtree: true });
  }

  #prices() {
    const json = this.querySelector('script[data-payflex-prices]')?.textContent;
    if (!json) return null;

    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  #key() {
    const { merchant = '', amount = '', min = '', max = '' } = this.dataset;
    return `${merchant}|${amount}|${min}|${max}`;
  }

  #render() {
    const mount = this.querySelector('[data-payflex-mount]');
    const { merchant, amount, min = '10', max = '20000' } = this.dataset;

    if (!mount || !merchant || !amount || Number(amount) <= 0) return;

    document.getElementById('payflex-modal')?.remove();

    const src = new URL(
      `https://widgets.payflex.co.za/${encodeURIComponent(merchant)}/payflex-widget-${WIDGET_VERSION}.js`
    );
    src.searchParams.set('type', 'calculator');
    src.searchParams.set('logo_type', 'navy');
    src.searchParams.set('min', min);
    src.searchParams.set('max', max);
    src.searchParams.set('amount', amount);

    const script = document.createElement('script');
    script.async = true;
    script.type = 'application/javascript';
    script.src = src.toString();

    mount.replaceChildren(script);
  }
}

if (!customElements.get('payflex-widget')) {
  customElements.define('payflex-widget', PayflexWidget);
}